/**
 * How one live went, in Season: its numbers, who came, the questions left, the replay, the busiest moments and who
 * reacted most. The room's Last live section and a session's own page draw it from the same read.
 */
import { useState } from "react";
import { Link } from "react-router";
import type { WebinarLive } from "../../../shared/types";
import { Fold } from "../../components/season/Fold";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { SeasonCard } from "../../components/season/Card";
import { GroupHead } from "../../components/season/PageHead";
import { Button } from "../../components/ui/Button";
import { Face, Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Pill } from "../../components/ui/Pill";
import { askedAt, replaySteps } from "./liveWords";
import { RepliesSheet } from "./RepliesSheet";
import { ReplaySheet } from "./ReplaySheet";
import { mins, type useSessionReport } from "./useSessionReport";

type Report = ReturnType<typeof useSessionReport>;

const n = (v: number) => v.toLocaleString("en-US");
/** The faces on the wall before "+N": a phone's width holds fewer. */
const wallOf = (layout: SeasonLayout) => (layout === "phone" ? 9 : 16);

function Numbers({ live, r, layout }: { live: WebinarLive; r: Report; layout: SeasonLayout }) {
  const items: Array<[string, string, string]> = [
    [n(live.cameLive), "Came live", `of ${n(live.registered)} registered`],
    [`${mins(r.report?.averageStaySec ?? 0)} min`, "Average stay", `of ${mins(r.lengthSec)} minutes`],
    [n(live.stayedToEnd), "Stayed to the end", `${r.stayedPct}% of those who came`],
    [n(r.report?.mostAtOnce ?? live.mostAtOnce), "Most at once", r.report ? `at ${mins(r.report.mostAtOnceAt)} min` : ""],
  ];
  return (
    <ul className={layout === "upright" ? "k-mets rm-m4 rm-m4-wide" : "k-mets rm-m4"}>
      {items.map(([value, label, sub]) => (
        <li key={label} className="k-met">
          <b>{value}</b>
          <span>{label}</span>
          {sub ? <i className="rm-sub">{sub}</i> : null}
        </li>
      ))}
    </ul>
  );
}

function Who({ live, r, layout, webinarId }: { live: WebinarLive; r: Report; layout: SeasonLayout; webinarId: string }) {
  const wall = (r.came?.people ?? []).slice(0, Math.min(wallOf(layout), live.cameLive));
  const people = `/people?webinar=${encodeURIComponent(webinarId)}`;
  return (
    <>
      <GroupHead
        title="Who came"
        count={n(live.cameLive)}
        action={
          live.cameLive ? (
            <Link className="k-link" to={people}>
              See them as a list <Icon name="chev" />
            </Link>
          ) : undefined
        }
      />
      <SeasonCard className="rm-wall">
        {live.cameLive === 0 ? (
          <p className="k-rm">No one came to this one.</p>
        ) : (
          <>
            {wall.map((p) => (
              <Link key={p.registrantId} to={`/people/${p.registrantId}`} aria-label={p.name}>
                <Ring name={p.name} photo={p.avatar_url} size={40} percent={p.pct} />
              </Link>
            ))}
            {r.came && live.cameLive > wall.length ? (
              <Link className="rm-plus" to={people} aria-label={`See all ${n(live.cameLive)}`}>
                +{n(live.cameLive - wall.length)}
              </Link>
            ) : null}
          </>
        )}
      </SeasonCard>
    </>
  );
}

function Questions({ live, r, layout }: { live: WebinarLive; r: Report; layout: SeasonLayout }) {
  const waiting = r.waiting.length;
  const status = live.questions.length === 0 ? "No one asked a question" : waiting ? `${n(waiting)} still ${waiting === 1 ? "needs" : "need"} an answer` : "All answered";
  return (
    <>
      <div className="k-gh rm-gh2">
        <h2>Questions</h2>
        <b>{status}</b>
        {waiting && layout !== "phone" ? (
          <Button className="rm-hr" icon="sparkle" onClick={() => r.setReplying(true)}>
            Send replies
          </Button>
        ) : null}
      </div>
      {live.questions.length ? (
        <GroupedList>
          {live.questions.map((q) => {
            const done = q.answered !== null || r.answered.includes(q.id);
            const text = (
              <>
                <bdi>{q.name}</bdi> <em className="rm-at">{askedAt(q.at)}</em>
              </>
            );
            const sub = (
              <>
                <span className="k-clamp">&ldquo;{q.body}&rdquo;</span>
                {done ? (
                  <span className="rm-sent">
                    <Icon name="check" /> {q.answered === "live" ? "Answered live" : "Answered"}
                  </span>
                ) : null}
              </>
            );
            return done ? (
              <ListRow key={q.id} lead={<Face name={q.name} size={44} />} title={text} sub={sub} />
            ) : (
              <ListRow
                key={q.id}
                lead={<Face name={q.name} size={44} />}
                title={text}
                sub={sub}
                value={<span className="k-hot">Answer</span>}
                onClick={() => void r.openThread(q)}
              />
            );
          })}
        </GroupedList>
      ) : null}
    </>
  );
}

function Replay({ live, r, layout }: { live: WebinarLive; r: Report; layout: SeasonLayout }) {
  const chapters = live.replay.chapters;
  if (layout === "phone") {
    return (
      <>
        <GroupHead title="The replay" />
        <GroupedList>
          <ListRow
            icon="play"
            tone="good"
            title="Watch the replay"
            sub={chapters > 0 ? `Wrote ${chapters} ${chapters === 1 ? "chapter" : "chapters"}` : r.pill.label}
            end={
              <Pill tone={r.pill.tone} dot={r.pill.tone !== "plain"}>
                {r.pill.label}
              </Pill>
            }
            {...(r.playable ? { onClick: () => r.setPlaying(0) } : {})}
          />
        </GroupedList>
      </>
    );
  }
  return (
    <SeasonCard>
      <h2 className="k-card-h">
        The replay
        <Pill tone={r.pill.tone} dot={r.pill.tone !== "plain"}>
          {r.pill.label}
        </Pill>
      </h2>
      <ul className="rm-done">
        {replaySteps(live).map((s) => (
          <li key={s.label} className={s.state}>
            <Icon name={s.state === "done" ? "check" : s.state === "missed" ? "x" : "hourglass"} />
            {s.label}
          </li>
        ))}
      </ul>
      <Button icon="play" disabled={!r.playable} onClick={() => r.setPlaying(0)}>
        Watch the replay
      </Button>
    </SeasonCard>
  );
}

function Moments({ r }: { r: Report }) {
  if (!r.marks.length) return null;
  return (
    <>
      <GroupHead title="The busiest moments" />
      <GroupedList>
        {r.marks.map((m, i) => (
          <ListRow
            key={m.at}
            lead={<span className="rm-n brass">{i + 1}</span>}
            title={`${mins(m.at)} min`}
            sub={`${m.words}${m.extra ? `. ${m.extra}` : ""}`}
            {...(r.playable ? { onClick: () => r.setPlaying(Math.max(0, m.at - 4)) } : {})}
          />
        ))}
      </GroupedList>
    </>
  );
}

function Reacted({ r }: { r: Report }) {
  const list = r.reactions?.reactors.slice(0, 3) ?? [];
  return (
    <>
      <GroupHead title="Who reacted most" />
      {list.length ? (
        <GroupedList>
          {list.map((p) => (
            <ListRow
              key={p.registrantId}
              lead={<Face name={p.name} size={44} />}
              title={p.name}
              sub={`${n(p.taps)} ${p.taps === 1 ? "reaction" : "reactions"}`}
              end={<Icon name="heart" />}
              to={`/people/${p.registrantId}`}
            />
          ))}
        </GroupedList>
      ) : (
        <p className="k-rm rm-none">{r.reactions ? "No one reacted this time." : "Loading"}</p>
      )}
    </>
  );
}

/** The replay, the busiest moments and who reacted: the rest of the report, or the iPad's right pane. */
export function ReportRest({ live, r, layout }: { live: WebinarLive; r: Report; layout: SeasonLayout }) {
  const [more, setMore] = useState(false);
  if (layout === "phone") {
    return (
      <>
        <Replay live={live} r={r} layout={layout} />
        {more ? (
          <>
            <Moments r={r} />
            <Reacted r={r} />
          </>
        ) : (
          <Fold title="More" line="Busiest moments, who reacted most" isOpen={false} onClick={() => setMore(true)} />
        )}
      </>
    );
  }
  if (layout === "upright") {
    return (
      <>
        <div className="k-cols rm-cols2">
          <div>
            <Replay live={live} r={r} layout={layout} />
          </div>
          <div>
            <Reacted r={r} />
          </div>
        </div>
        <Moments r={r} />
      </>
    );
  }
  return (
    <>
      <Replay live={live} r={r} layout={layout} />
      <Moments r={r} />
      <Reacted r={r} />
    </>
  );
}

/** The report's numbers, who came and the questions: everything the middle of the page holds. */
export function ReportMain({ live, r, layout, webinarId }: { live: WebinarLive; r: Report; layout: SeasonLayout; webinarId: string }) {
  return (
    <>
      <Numbers live={live} r={r} layout={layout} />
      <Who live={live} r={r} layout={layout} webinarId={webinarId} />
      <Questions live={live} r={r} layout={layout} />
    </>
  );
}

/** The replay player and the replies sheet the report opens. */
export function ReportSheets({ webinarId, title, r }: { webinarId: string; title: string; r: Report }) {
  return (
    <>
      {r.replying ? <RepliesSheet webinarId={webinarId} questions={r.waiting} onClose={() => r.setReplying(false)} onSent={r.markAnswered} /> : null}
      {r.playing !== null ? (
        <ReplaySheet webinarId={webinarId} sessionId={r.sessionId} title={title} startSec={r.playing} chapters={r.chapters} onClose={() => r.setPlaying(null)} />
      ) : null}
    </>
  );
}
